// One decision for two situations that look different and need the same care:
// a lazy import that failed, and a service worker waiting to activate.
//
// The crucial distinction, and the reason this is not just "reload":
// A FAILED IMPORT DOES NOT PROVE AN UPDATE EXISTS. The chunk may be missing
// because a deploy replaced it, or because the network dropped, or because
// the host returned something that was not JavaScript. Telling someone "a new
// version is available" when their wifi blipped is a guess presented as fact,
// so the copy says what is actually known: this part could not load.

export type Trigger = 'chunk-error' | 'worker-waiting';

export interface RecoveryInput {
  trigger: Trigger;
  online: boolean;
  /** Work at risk in THIS tab. */
  unsaved: number;
  pending: number;
  /** Work at risk in other tabs, or uncertainty about them. */
  otherTabsBlocking: boolean;
  /** Reloads already attempted for this trigger in this tab. */
  reloadAttempts: number;
  /** Milliseconds until another attempt is allowed; 0 when one is allowed
   *  now. Only meaningful once reloadAttempts has reached the cap. */
  cooldownRemainingMs?: number;
}

/** Two reloads have not fixed it; a third immediately will not either, and
 *  an app that reloads itself forever is worse than one that pauses.
 *  ⚠️ A PAUSE, not a refusal — see ATTEMPT_WINDOW_MS. */
export const MAX_RELOADS = 2;

/** "in about 3 minutes" reads better than "in 2m 47s" and is honest enough. */
const minutesLeft = (ms: number): string => {
  const m = Math.ceil(ms / 60_000);
  return m <= 1 ? 'in about a minute' : `in about ${m} minutes`;
};

/** The way out that always works, whatever else is wrong. ⚠️ Leads with
 *  saving: this is shown to someone who may be holding unsaved work, and
 *  telling them to close the window first would be how they lose it. */
export const CLOSE_AND_REOPEN =
  'Save any unsaved work, then close this window and reopen KaziPeople.';

export interface RecoveryPlan {
  title: string;
  body: string;
  canRetry: boolean;
  canReload: boolean;
  /** Why reload is unavailable, when it is. */
  blockedReason: string | null;
  /** True when we are confident a new version exists, not merely guessing. */
  updateKnown: boolean;
}

export const planRecovery = (i: RecoveryInput, holdReason: string | null, tabReason: string | null): RecoveryPlan => {
  const updateKnown = i.trigger === 'worker-waiting';
  const title = updateKnown
    ? 'A new version of KaziPeople is ready'
    : 'This part of KaziPeople couldn’t load';
  const body = updateKnown
    ? 'Reload when you are ready. Nothing is lost by waiting.'
    : i.online
      ? 'It may have been updated while this page was open, or the file may not have arrived. Getting it back needs a reload.'
      : 'You appear to be offline. Reconnect, then reload to get the missing part.';

  // There is no Retry.
  //
  // The obvious design re-invokes the lazy import. Two things stopped that:
  // re-importing the SAME specifier cannot restore a chunk the deploy
  // deleted, and inventing a fresh URL (a ?t= parameter) does not either —
  // it just asks for a file that is still missing, while interfering with
  // Vite's dependency resolution.
  //
  // Whether the loader recovers from a genuinely TRANSIENT failure was not
  // established: on dev a missing chunk fails permanently (the host returns
  // HTML, so the MIME check rejects it) and no clean transient case could be
  // produced from the page. Offering a button that may do nothing is the
  // thing this whole flow exists to avoid, so recovery is a reload and the
  // copy says so.
  const loopedOut = i.reloadAttempts >= MAX_RELOADS;

  // Two different things were conflated here, and driving the real flow
  // exposed it: a reason to SHOW, and a reason to FORBID.
  //
  // Only this window's own state can forbid the press. Something another
  // window is holding is advisory — the copy literally says "Finish or close
  // it, then update", and treating it as a blocker disabled the very button
  // that sentence tells you to press. One refusal then wedged the prompt
  // permanently, because the deferral is cleared inside the handler the
  // disabled button prevents from running.
  const forbid =
    holdReason ? holdReason
    : !i.online ? 'You appear to be offline. Reloading now would not fetch the new version.'
    // ⚠️ Tell them what to DO. The old wording — "report this rather than
    // retrying" — left someone staring at a disabled button with no route
    // forward and an action only we could take. Closing the tab clears the
    // per-tab counter, which is the actual fix available to them.
    // ⚠️ Tell them what to DO, and when they may try again. The old wording
    // — "report this rather than retrying" — left someone at a disabled
    // button with no route forward and an action only we could take.
    : loopedOut
      ? `Reloading has not helped. ${CLOSE_AND_REOPEN} You can try again ${minutesLeft(i.cooldownRemainingMs ?? 0)}.`
    : null;
  const blockedReason = forbid ?? tabReason;

  return {
    title,
    body,
    canRetry: false,
    canReload: forbid === null,
    blockedReason,
    updateKnown,
  };
};

// ---- reload-loop accounting --------------------------------------------
// Per tab and per trigger, in sessionStorage: it must survive the reload it
// is counting (so not memory) and must not leak to the next session or to
// other tabs (so not localStorage).

export const attemptKey = (t: Trigger) => `kazi-recovery-reloads:${t}`;

/**
 * How long the cap lasts.
 *
 * ⚠️ It used to last for the tab's whole life, which turned a transient
 * failure into a permanent dead end: a panel saying an update is ready, a
 * disabled button, and no way out unless you happened to know that opening a
 * new tab clears it. Two failures in a row justify pausing; they do not
 * justify refusing forever.
 */
export const ATTEMPT_WINDOW_MS = 5 * 60 * 1000;

/** Stored as `count:lastAttemptEpochMs`. */
const parse = (raw: string | null): { count: number; at: number } => {
  if (!raw) return { count: 0, at: 0 };
  const [c, a] = raw.split(':');
  const count = Number(c);
  const at = Number(a);
  return {
    count: Number.isFinite(count) && count >= 0 ? count : 0,
    // A bare number is the old format, which carried no time. Treating it as
    // long ago is deliberate: tabs already stuck under the old behaviour
    // recover on their next load instead of staying stuck.
    at: Number.isFinite(at) ? at : 0,
  };
};

export const readAttempts = (
  t: Trigger,
  store: Storage | null = safeSession(),
  now: number = Date.now(),
): number => {
  try {
    const { count, at } = parse(store?.getItem(attemptKey(t)) ?? null);
    if (count === 0) return 0;
    // A clock that moved backwards must not extend the pause.
    if (at > now) return 0;
    return now - at >= ATTEMPT_WINDOW_MS ? 0 : count;
  } catch { return 0; }
};

export const noteAttempt = (
  t: Trigger,
  store: Storage | null = safeSession(),
  now: number = Date.now(),
): void => {
  try {
    store?.setItem(attemptKey(t), `${readAttempts(t, store, now) + 1}:${now}`);
  } catch { /* private mode */ }
};

/** Called once the app has loaded successfully, which is the only real proof
 *  the problem is gone. */
export const clearAttempts = (t: Trigger, store: Storage | null = safeSession()): void => {
  try { store?.removeItem(attemptKey(t)); } catch { /* private mode */ }
};

/** How long until another attempt is allowed. 0 when one is allowed now. */
export const cooldownRemainingMs = (
  t: Trigger,
  store: Storage | null = safeSession(),
  now: number = Date.now(),
): number => {
  try {
    const { count, at } = parse(store?.getItem(attemptKey(t)) ?? null);
    if (count < MAX_RELOADS || at <= 0 || at > now) return 0;
    return Math.max(0, ATTEMPT_WINDOW_MS - (now - at));
  } catch { return 0; }
};

function safeSession(): Storage | null {
  try { return typeof sessionStorage === 'undefined' ? null : sessionStorage; } catch { return null; }
}
